import React, { FC } from "react";
import {
    BatchBuildsData,
    BatchBuildsVariables,
    DropdownAction,
    Revision
} from "../../models/types";
import { useMatch, useNavigate } from "react-router-dom";
import {
    Button,
    Card,
    Col,
    OverlayTrigger,
    Row,
    Tooltip
} from "react-bootstrap";
import { useQuery } from "@apollo/client";
import { GET_BATCH_BUILDS } from "../../graphql/queries";
import { checkBuildsStatus, getFormattedTime } from "../../common/Helpers";
import { ActionDropdown } from "../dropdowns/ActionDropdown";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars, faPlay } from "@fortawesome/free-solid-svg-icons";
import cx from "classnames";
import styles from "./RevisionItem.module.scss";
// @ts-ignore
import "./simple.scss";

interface Props {
    revision: Revision;
    projectId: number;
    developerMode: boolean;
    showArchived: boolean;
    showReleaseNotes: () => void;
    onArchive: () => void;
    onCreate: () => void;
    onRemove: () => void;
}

export const RevisionItem: FC<Props> = ({
    revision,
    projectId,
    developerMode,
    showArchived,
    showReleaseNotes,
    onArchive,
    onCreate,
    onRemove
}) => {
    const navigate = useNavigate();
    const match = useMatch("/:path/*");
    const { data } = useQuery<BatchBuildsData, BatchBuildsVariables>(
        GET_BATCH_BUILDS,
        {
            variables: { revisionId: revision.id },
            fetchPolicy: "network-only",
            skip: revision === undefined
        }
    );

    if (!revision || (!developerMode && !showArchived && revision.archived)) {
        return null;
    }

    const { hasBuilds } = checkBuildsStatus(data);
    const productId = revision.project.product?.id;
    const variationsPath = `${match?.pathname}/${productId}/projects/${projectId}/revisions/${revision.id}/variations`;
    const mutatorPath = `${variationsPath}/customator`;
    const historyPath = `${match?.pathname}/${productId}/projects/${projectId}/revisions/${revision.id}/history`;

    const actions: DropdownAction[] = developerMode
        ? [
              {
                  header: "Edit",
                  variant: "inverse",
                  callback: () => navigate(variationsPath)
              },
              {
                  header: "Show Build History",
                  variant: hasBuilds ? "inverse" : "disabled",
                  className: hasBuilds ? undefined : "disabled",
                  callback: () => {
                      if (hasBuilds) {
                          navigate(historyPath);
                      }
                  }
              },
              {
                  header: "Changelog",
                  variant: "inverse",
                  callback: showReleaseNotes
              },
              {
                  header: "Duplicate",
                  variant: "inverse",
                  callback: onCreate
              },
              {
                  header: "Remove",
                  variant: "danger",
                  callback: onRemove
              }
          ]
        : [];

    const edit = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        if (e.defaultPrevented) return;
        developerMode ? navigate(variationsPath) : navigate(mutatorPath);
    };

    return (
        <Card className={cx("inner-card", styles.card)} onClick={edit}>
            <Card.Header className="text-contrast mouseHover custom-card-product">
                <Row className="d-flex bread-text align-items-center no-wrap">
                    <Col
                        className={cx("text-start", {
                            "col-6": developerMode,
                            "col-5": !developerMode
                        })}
                    >
                        <span
                            className={cx(
                                "text-contrast ps-2",
                                styles.revisionName
                            )}
                        >
                            {revision.name}
                        </span>
                    </Col>
                    <Col
                        className={cx(
                            "d-flex ps-0 no-wrap justify-content-center text-subtle",
                            developerMode
                                ? "col-1 xs-font"
                                : "analytics-font col-3"
                        )}
                    >
                        {getFormattedTime(revision.dateUpdated)}
                    </Col>
                    {developerMode ? (
                        <Col className="text-start col-2"></Col>
                    ) : null}
                    <Col
                        className={cx(
                            "d-flex justify-content-end pe-1",
                            developerMode ? "col-1" : "col-4"
                        )}
                    >
                        {developerMode ? (
                            <ActionDropdown
                                id={`revision-${revision.id}`}
                                variant="info"
                                actions={actions}
                            />
                        ) : (
                            <>
                                <OverlayTrigger
                                    delay={{ show: 550, hide: 100 }}
                                    overlay={
                                        <Tooltip>
                                            Go to Customator with this project
                                        </Tooltip>
                                    }
                                >
                                    <span
                                        className={cx(
                                            styles.customatorHint,
                                            "text-contrast mx-4"
                                        )}
                                    >
                                        <FontAwesomeIcon
                                            icon={faPlay}
                                            size="xs"
                                            className={cx(
                                                "me-2",
                                                styles.customatorIcon
                                            )}
                                        />
                                        Customator
                                    </span>
                                </OverlayTrigger>
                                <OverlayTrigger
                                    delay={{ show: 550, hide: 100 }}
                                    overlay={
                                        <Tooltip>
                                            Show all the customator builds for
                                            this project
                                        </Tooltip>
                                    }
                                >
                                    <span className="mx-2">
                                        <Button
                                            size="sm"
                                            variant="primary"
                                            disabled={!hasBuilds}
                                            className={styles.historyButton}
                                            onClick={e => {
                                                e.preventDefault();
                                                if (hasBuilds) {
                                                    navigate(historyPath);
                                                }
                                            }}
                                        >
                                            <FontAwesomeIcon
                                                icon={faBars}
                                                className="me-2"
                                                size="xs"
                                            />
                                            Build History
                                        </Button>
                                    </span>
                                </OverlayTrigger>
                            </>
                        )}
                    </Col>
                </Row>
            </Card.Header>
        </Card>
    );
};
